import type { IStoryblokStorySeo, IStoryblokStoryHreflangTag } from '../../../ui/types/seo/StoryblokAPI';
import type { RouteLocationNormalizedLoaded } from 'vue-router';

export function setSeoMeta(
    seoMetaTags: IStoryblokStorySeo[],
    config: { STORYBLOK_VERSION: string; STORYBLOK_FOLDER: string },
    route: RouteLocationNormalizedLoaded
) {
    if (!seoMetaTags) {
        return;
    }

    const siteUrl = useRuntimeConfig().public?.SITE_URL;
    const hreflangTags = generateHreflangLinks(seoMetaTags[0]?.hreflang_tags || [], siteUrl);

    useHead({
        link: [
            {
                rel: 'canonical',
                href: seoMetaTags[0]?.canonical || siteUrl + route.path,
            },
            ...hreflangTags,
        ],
    });

    useSeoMeta({
        title: () => seoMetaTags[0]?.title ?? '',
        description: () => seoMetaTags[0]?.description ?? '',
        robots: () => (config.STORYBLOK_VERSION === 'published' ? seoMetaTags[0]?.robots : 'nofollow,noindex'),
        ogTitle: () => seoMetaTags[0]?.title ?? '',
        ogDescription: () => seoMetaTags[0]?.description ?? '',
        ogImage: () => seoMetaTags[0]?.og_image?.filename ?? '',
        ogUrl: () => seoMetaTags[0]?.og_url ?? '',
        twitterSite: () => seoMetaTags[0]?.twitter_site ?? '',
        twitterCard: () => {
            const card = seoMetaTags[0]?.twitter_card;
            if (card === 'app' || card === 'summary' || card === 'summary_large_image' || card === 'player') {
                return card;
            }
            return undefined;
        },
        twitterTitle: () => seoMetaTags[0]?.twitter_title ?? '',
        twitterDescription: () => seoMetaTags[0]?.twitter_description ?? '',
        twitterCreator: () => seoMetaTags[0]?.twitter_creator ?? '',
        twitterImage: () => seoMetaTags[0]?.twitter_image?.filename ?? '',
    });
}

export function generateHreflangLinks(
    hreflangTagsRaw: IStoryblokStoryHreflangTag[] = [],
    siteUrl: string
): { rel: string; hreflang: string; href: string }[] {
    const xDefaultTag = hreflangTagsRaw.find((tag) => tag.x_default);
    const tags = hreflangTagsRaw.filter((tag) => {
        return !tag.x_default;
    });

    const links = tags.map((tag) => {
        let lang = tag.language === 'default' ? 'en' : tag.language;

        if (tag.include_country_code && tag.country_code) {
            lang = `${lang}-${tag.country_code}`;
        }

        return {
            rel: 'alternate',
            hreflang: lang,
            href: `${siteUrl}/${tag.slug.replace(/^\/+/, '')}`,
        };
    });

    // Self-referencing link
    if (tags.length > 0 && xDefaultTag?.slug) {
        const xDefaultHref = xDefaultTag?.slug;

        links.push({
            rel: 'alternate',
            hreflang: 'x-default',
            href: `${siteUrl.replace(/\/+$/, '')}/${xDefaultHref.replace(/^\/+/, '')}`,
        });
    }

    return links;
}

function getLangFromRoutePath(route: RouteLocationNormalizedLoaded): string {
    // Extract the first path segment (assumed to be the current locale)
    const lang = route.path.replace(/^\/+/, '').split('/')[0] || '';
    return lang.replace(/\buk\b/g, 'en');
}
